// src/pages/Dashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Loader2,
    TrendingUp,
    Package,
    ShoppingCart,
    Wallet,
} from 'lucide-react';
import { dashboardAPI } from '../services/api';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from 'recharts';

type Period = 'day' | 'week' | 'month' | 'year' | 'all';

const fa = new Intl.NumberFormat('fa-IR');

const periodLabel = (p: Period) => {
    switch (p) {
        case 'day': return 'روز';
        case 'week': return 'هفته';
        case 'month': return 'ماه';
        case 'year': return 'سال';
        default: return 'همه';
    }
};

const Dashboard: React.FC = () => {
    const { client } = useAuth();
    const [period, setPeriod] = useState<Period>('month');
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchDashboardData(period);
    }, [period]);

    const fetchDashboardData = async (prd: Period) => {
        setLoading(true);
        setError('');
        try {
            const result = await dashboardAPI.getDashboard(prd);
            setData(result);
        } catch (err: any) {
            setError(err?.message || 'خطا در دریافت اطلاعات داشبورد');
        } finally {
            setLoading(false);
        }
    };

    const totalSpent = Number(data?.total_spent ?? 0);
    const activeLicenses = Number(data?.active_licenses_count ?? 0);
    const walletBalance = Number(data?.wallet_balance ?? 0);
    const lastOrders = data?.last_orders ?? [];

    const chartData = useMemo(() => {
        const raw = data?.chart ?? [];
        return raw.map((p: any) => ({
            date: new Date(p.date).toLocaleDateString('fa-IR'),
            total: Number(p.total_spent ?? 0),
        }));
    }, [data]);

    const totalChartSales = useMemo(
        () => chartData.reduce((sum, p) => sum + (p.total || 0), 0),
        [chartData]
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    const stats = [
        {
            title: 'مجموع پرداختی‌ها',
            value: `${fa.format(totalSpent / 10)} تومان`,
            icon: TrendingUp,
            gradient: 'from-green-500 to-emerald-500',
            link: '/wallet',
        },
        {
            title: 'لایسنس‌های فعال',
            value: fa.format(activeLicenses),
            icon: Package,
            gradient: 'from-emerald-600 to-green-700',
            link: '/licenses',
        },
        {
            title: 'موجودی کیف‌پول',
            value: `${fa.format(walletBalance / 10)} تومان`,
            icon: Wallet,
            gradient: 'from-green-500 to-cyan-500',
            link: '/wallet',
        },
        {
            title: 'جمع سفارشات نمودار',
            value: `${fa.format(totalChartSales / 10)} تومان`,
            icon: ShoppingCart,
            gradient: 'from-teal-500 to-cyan-600',
            link: '/orders',
        },
    ];

    return (
        <div className="space-y-6 p-4 md:p-8 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50 min-h-screen" dir="rtl">
            {/* Header */}
            <div className="p-5 md:p-8 rounded-3xl bg-gradient-to-r from-green-700 to-emerald-600 text-white shadow-xl">
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                    ✨ <span>خوش آمدید</span>
                </h1>
                <p className="text-green-100 mt-2 text-xs md:text-sm">
                    خلاصه وضعیت خرید و مالی شما در لایسنس‌اپ
                </p>
            </div>

            {/* Period Filter */}
            <div className="bg-white/90 shadow-md rounded-2xl border border-green-100 px-2 py-2">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-xs hidden sm:block md:text-sm text-green-900 font-medium">
                        بازه زمانی گزارش
                    </span>
                    <div className="flex-1 w-full md:w-auto overflow-x-auto scrollbar-none">
                        <div className="flex gap-1.5 md:gap-2 justify-between md:justify-end min-w-max">
                            {(['day','week','month','year','all'] as Period[]).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setPeriod(p)}
                                    className={[
                                        'px-3.5 py-1.5 rounded-full text-[11px] md:text-sm transition font-medium whitespace-nowrap',
                                        period === p
                                            ? 'bg-green-600 text-white shadow'
                                            : 'text-green-700 hover:bg-green-50',
                                    ].join(' ')}
                                >
                                    {periodLabel(p)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <Link key={index} to={stat.link}>
                            <Card className="hover:shadow-xl transition-all duration-300 border-green-200 bg-white/95 backdrop-blur hover:scale-[1.02] cursor-pointer group">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-green-800 group-hover:text-green-900">
                                        {stat.title}
                                    </CardTitle>
                                    <div className={`p-3 rounded-full bg-gradient-to-r ${stat.gradient} shadow-lg`}>
                                        <Icon className="h-5 w-5 text-white" />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-xl md:text-2xl font-bold text-green-900 mb-1">
                                        {stat.value}
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    );
                })}
            </div>

            {/* Chart */}
            <Card className="border-green-200 bg-white/95 backdrop-blur-sm shadow-md">
                <CardHeader>
                    <CardTitle className="text-green-900">نمودار پرداخت‌ها</CardTitle>
                    <CardDescription className="text-green-700">
                        مجموع پرداخت‌های روزانه در بازه‌ی {periodLabel(period)}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {chartData.length === 0 ? (
                        <div className="py-10 text-center text-green-700">
                            داده‌ای برای نمایش وجود ندارد
                        </div>
                    ) : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis tickFormatter={(v) => fa.format(v / 10)} />
                                    <Tooltip
                                        formatter={(value: any) => [`${fa.format(value / 10)} تومان`, 'پرداخت']}
                                        labelFormatter={(label) => `تاریخ: ${label}`}
                                    />
                                    <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={3} dot />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card className="border-green-200 bg-white/95 backdrop-blur-sm shadow-md">
                <CardHeader>
                    <CardTitle className="text-green-900">سفارش‌های اخیر</CardTitle>
                    <CardDescription className="text-green-700">
                        آخرین سفارش‌های ثبت‌شده
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {lastOrders.length === 0 ? (
                        <div className="py-6 text-center text-green-700">
                            هیچ سفارشی ثبت نشده است.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-right text-sm">
                                <thead>
                                <tr className="bg-green-50 text-green-700">
                                    <th className="py-2 px-3 font-medium">شناسه</th>
                                    <th className="py-2 px-3 font-medium">محصول</th>
                                    <th className="py-2 px-3 font-medium">مبلغ</th>
                                    <th className="py-2 px-3 font-medium">وضعیت</th>
                                    <th className="py-2 px-3 font-medium">تاریخ</th>
                                </tr>
                                </thead>
                                <tbody>
                                {lastOrders.map((order: any) => (
                                    <tr key={order.id} className="border-b last:border-0 hover:bg-green-50/40">
                                        <td className="py-2 px-3">#{order.id}</td>
                                        <td className="py-2 px-3">{order.product_title}</td>
                                        <td className="py-2 px-3">{fa.format(Number(order.amount) / 10)} تومان</td>
                                        <td className="py-2 px-3">
                                            <span className={`px-2 py-1 rounded-full text-xs ${
                                                order.status === 'paid' ? 'bg-green-100 text-green-800' :
                                                    order.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                            }`}>
                                                {order.status === 'paid' ? 'پرداخت‌شده' :
                                                    order.status === 'pending_payment' ? 'در انتظار پرداخت' :
                                                        order.status}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3">{new Date(order.created_at).toLocaleDateString('fa-IR')}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Dashboard;
